import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import videojs from "video.js";
import { useHistory, useLocation } from "react-router-dom";

import {
  Context,
  createVideoChapterViewLog,
  fetchCompletionStatuses,
  fetchLessonRetrospectives,
  fetchLessonVideoChapters,
  postCompletedLesson,
  postLessonRetrospectives,
  projectionPath,
  fetchLesson,
  fetchTeachingMaterial,
  notFoundPath,
} from "../../../teacherStore";
import VideoLesson from "../../../templates/teacher/VideoLesson";
import { parseQueryParams } from "../../../libs/Util";

export default () => {
  const { state, dispatch } = useContext(Context);
  const history = useHistory();
  const params = parseQueryParams();
  const lessonId = parseInt(params["lesson"]);
  const playingChapterId = parseInt(params["chapter"]);
  const classListInParams = params["classList"];
  const { pathname, search, state: locationState } = useLocation<{
    fromProjection?: boolean;
    lastProjectionPage?: number;
    videoCurrentTime?: number;
  }>();
  const playerRef = useRef<videojs.Player | null>(null);
  const lastProjectionPageRef = useRef(locationState?.lastProjectionPage || 1);
  const fromProjectionRef = useRef(locationState?.fromProjection);
  // レンダリングのたびにclassListが異なるメモリに作成され
  // 再度useEffect内の処理が実行してしまうためメモ化
  const classList = useMemo<number[]>(
    () =>
      classListInParams
        ? classListInParams.split(",").map((classID: string) => Number(classID))
        : [],
    [classListInParams],
  );
  const teachingMaterialId = state.lesson_video_chapters?.teaching_material_id;
  const currentMaterialId = state.teaching_material?.id;
  const isFromProjection = fromProjectionRef.current ?? false;
  const canSwitchToSlide =
    state.lesson?.id === lessonId && currentMaterialId === teachingMaterialId;

  const completion_statuses = state.completion_statuses.completion_statuses;
  const hasLessonCompletion =
    completion_statuses.length === 0 ||
    completion_statuses.some(status => status.is_completed);

  const postCompletedLessonFunc = () => {
    dispatch(postCompletedLesson(classList, lessonId));
  };

  const postCompletedLessonRetrospectivesFunc = (
    score: number,
    lessonDeliveryScore: number,
    groupWorkScore: number | null,
  ) => {
    dispatch(
      postLessonRetrospectives(
        state.teacher.id,
        classList.join(),
        lessonId,
        score,
        lessonDeliveryScore,
        groupWorkScore,
      ),
    );
  };

  // ブラウザリロードした時に、途中から再生されないように必要ないstateを消去
  useEffect(() => {
    if (locationState?.fromProjection) {
      history.replace({
        pathname,
        search,
        state: {
          lastProjectionPage: locationState.lastProjectionPage,
          videoCurrentTime: locationState.videoCurrentTime,
        },
      });
    }
  }, [history, search, locationState, pathname]);

  // 関数更新により何度も関数が実行されてしまうため、useCallbackを使用
  const handleReadyFunc = useCallback((player: videojs.Player) => {
    playerRef.current = player;
  }, []);

  // 関数更新により何度も関数が実行されてしまうため、useCallbackを使用
  const handleFirstVideoPlayFunc = useCallback(
    (player: videojs.Player) => {
      isFromProjection && locationState?.videoCurrentTime
        ? player.currentTime(locationState.videoCurrentTime)
        : dispatch(
            createVideoChapterViewLog(
              lessonId,
              playingChapterId,
              JSON.stringify(classList),
            ),
          );
    },
    [
      isFromProjection,
      locationState,
      dispatch,
      lessonId,
      playingChapterId,
      classList,
    ],
  );

  const handleSwitchToSlide = () => {
    if (!state.lesson_video_chapters) return;

    history.push({
      pathname: projectionPath,
      search: `?teachingMaterial=${
        state.lesson_video_chapters.teaching_material_id
      }&lesson=${lessonId}&page=${
        lastProjectionPageRef.current
      }&classList=${classList.join(",")}`,
      state: {
        fromVideoLesson: true,
        lastChapter: playingChapterId,
        videoCurrentTime: playerRef.current?.currentTime(),
      },
    });
  };

  useEffect(() => {
    if (isFromProjection && state.completion_statuses?.lesson_id === lessonId)
      return;

    dispatch(fetchCompletionStatuses(classList, lessonId));
    dispatch(fetchLessonRetrospectives(classList.join(), lessonId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, classList, lessonId, isFromProjection]);

  // チャプター切り替え時にワークの完了状態を再取得するため
  // 依存配列にplayingChapterIdを含めている
  useEffect(() => {
    dispatch(fetchLessonVideoChapters(classList, lessonId));
  }, [dispatch, classList, lessonId, playingChapterId]);

  useEffect(() => {
    if (isFromProjection && state.lesson?.id === lessonId) return;
    dispatch(fetchLesson(lessonId, classList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, lessonId]);

  useEffect(() => {
    if (!teachingMaterialId) return;
    if (teachingMaterialId === currentMaterialId) return;
    dispatch(fetchTeachingMaterial(teachingMaterialId));
  }, [dispatch, teachingMaterialId, currentMaterialId]);

  useEffect(() => {
    const assignedClassIds = state.teacher.classes
      .filter(cls => !cls.is_sub)
      .map(cls => cls.id);

    if (assignedClassIds.length === 0) return;
    if (classList.some(cls_id => !assignedClassIds.includes(cls_id))) {
      history.replace(notFoundPath);
    }
  }, [state.teacher.classes, classList, history]);

  const allVideosUnavailable =
    state.lesson_video_chapters &&
    (state.lesson_video_chapters.video_chapters.length === 0 ||
      state.lesson_video_chapters.video_chapters.every(
        chapter => !chapter.url,
      ));

  if (allVideosUnavailable) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <p>動画教材がないためアクセスできません。</p>
      </div>
    );
  }

  return state.lesson_video_chapters ? (
    <VideoLesson
      lessonVideoChapters={state.lesson_video_chapters}
      playingChapterId={playingChapterId}
      classList={classList}
      prevLessonId={state.prev_video_lesson_id}
      prevLessonFirstChapterId={state.prev_video_lesson_first_chapter_id}
      nextLessonId={state.next_video_lesson_id}
      nextLessonFirstChapterId={state.next_video_lesson_first_chapter_id}
      hasLessonCompletion={hasLessonCompletion}
      teacherLessonRetrospectives={state.teacher_lesson_retrospectives}
      postCompletedLesson={postCompletedLessonFunc}
      postCompletedLessonRetrospectives={postCompletedLessonRetrospectivesFunc}
      handleReady={handleReadyFunc}
      handleFirstVideoPlay={handleFirstVideoPlayFunc}
      onSwitchToSlide={handleSwitchToSlide}
      canSwitchToSlide={canSwitchToSlide}
      isFromProjection={isFromProjection}
    />
  ) : null;
};
